const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
// Single source of truth for the backend mount point. Every backend route
// (auth + data) lives under this prefix; SPA routes (e.g. `/sign-in`) do not.
export const API_BASE = '/api/dashboard';
// PM Tools report service — a separate backend, proxied under this prefix in
// vite.config.ts. Report endpoints (weeks/files/generate) live here, not under API_BASE.
export const REPORTS_API_BASE = '/api/v1';
// Absolute base URL used only in display contexts (agent prompt text, external links).
// API calls use relative `API_BASE` paths through the Vite proxy.
export const DISPLAY_API_BASE_URL = import.meta.env.VITE_API_URL ?? window.location.origin;
const REFRESH_ENDPOINT = `${API_BASE}/auth/refresh`;
const LOGIN_ENDPOINT = '/sign-in';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export function readCsrf(cookieString: string = document.cookie): string {
  const match = cookieString.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  if (!match || match[1] === undefined) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function withDefaults(init: RequestInit = {}): RequestInit {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  // FormData must set its own `multipart/form-data; boundary=…` — never force JSON.
  if (
    init.body !== undefined &&
    init.body !== null &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  if (!SAFE_METHODS.has(method)) {
    headers.set(CSRF_HEADER_NAME, readCsrf());
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json, text/plain;q=0.9, */*;q=0.5');
  }

  return { ...init, credentials: 'include', headers };
}

type AuthedFetchOptions = {
  redirectOnAuthFailure?: boolean;
};

/**
 * `ETag` revalidation is meant to be invisible here. The backend serves its two
 * cacheable lists (`/projects/{id}/notes`, `/projects/{id}/tasks`) with a weak
 * `ETag` and `Cache-Control: private, no-cache`, so the browser's HTTP cache
 * stores the body, adds `If-None-Match` on its own, and replays a cached `200`
 * when the server answers `304`. We never see the `304`.
 *
 * A `304` reaching this function means the conditional request went out but no
 * cache entry could satisfy it — an intermediary added the header, or the entry
 * was evicted between request and response. There is no body to pair with the
 * validator, and `304` is not `res.ok`, so leaving it alone would surface as a
 * spurious error. Re-issue once with `cache: 'reload'`, which skips the local
 * cache and therefore sends no conditional header, guaranteeing a full body.
 */
async function fetchResolvingNotModified(
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  const res = await fetch(input, withDefaults(init));
  if (res.status !== 304) return res;
  return fetch(input, { ...withDefaults(init), cache: 'reload' });
}

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: AuthedFetchOptions = {},
): Promise<Response> {
  const { redirectOnAuthFailure = true } = options;
  const first = await fetchResolvingNotModified(input, init);
  if (first.status !== 401) return first;

  const refresh = await fetch(REFRESH_ENDPOINT, withDefaults({ method: 'POST' }));
  if (refresh.ok) {
    return fetchResolvingNotModified(input, init);
  }

  if (redirectOnAuthFailure && typeof window !== 'undefined') {
    window.location.assign(LOGIN_ENDPOINT);
  }
  throw new Error('session expired');
}
